/**
 * Agent list (§13) — dense operator table: name, status dot, engine,
 * assigned number, calls (7d), last updated. Row click opens the editor;
 * each row also carries quiet Duplicate / Delete actions.
 * Empty state carries a single Create agent action.
 *
 * GET /api/agents returns flat summaries with engine, assigned number, and
 * the 7-day call count pre-resolved server-side — no per-row fan-out.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { DeleteAgentModal } from "../components/DeleteAgentModal";
import { EmptyState } from "../components/EmptyState";
import { Spinner } from "../components/Spinner";
import { StatusDot, agentStatusTone } from "../components/StatusDot";
import { Table, TBody, TD, TH, THead, TR } from "../components/Table";
import { api } from "../lib/api";
import { formatPhone, formatRelative } from "../lib/format";
import type { AgentSummary } from "../lib/types";
import { ENGINE_LABELS_SHORT } from "../lib/types";

export default function AgentList() {
  const navigate = useNavigate();

  const [agents, setAgents] = useState<AgentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Per-row actions
  const [rowError, setRowError] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AgentSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    api.agents
      .list(controller.signal)
      .then(setAgents)
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Could not load your agents.");
      });
    return () => controller.abort();
  }, [reloadKey]);

  const loading = agents === null && !error;

  async function handleDuplicate(agent: AgentSummary): Promise<void> {
    if (duplicatingId) return;
    setRowError(null);
    setDuplicatingId(agent.id);
    try {
      await api.agents.duplicate(agent.id);
      setReloadKey((k) => k + 1); // the copy lands as a draft — refresh to show it
    } catch (err: unknown) {
      setRowError(
        err instanceof Error
          ? `Could not duplicate ${agent.name}. ${err.message}`
          : `Could not duplicate ${agent.name}. Try again.`,
      );
    } finally {
      setDuplicatingId(null);
    }
  }

  async function handleDelete(): Promise<void> {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.agents.remove(deleteTarget.id);
      setAgents((prev) => (prev ? prev.filter((a) => a.id !== deleteTarget.id) : prev));
      setDeleteTarget(null);
    } catch (err: unknown) {
      setDeleteError(
        err instanceof Error
          ? `Could not delete the agent. ${err.message}`
          : "Could not delete the agent. Try again.",
      );
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-xl tracking-display text-bright">Agents</h1>
          <p className="mt-1 text-sm text-muted">
            {agents === null
              ? "Loading…"
              : `${agents.length} agent${agents.length === 1 ? "" : "s"}`}
          </p>
        </div>
        {(agents?.length ?? 0) > 0 && (
          <Button variant="primary" onClick={() => navigate("/agents/new")}>
            Create agent
          </Button>
        )}
      </div>

      {error && (
        <div className="flex items-center justify-between rounded border border-danger/60 bg-danger/10 px-4 py-3">
          <p className="text-sm text-danger">{error}</p>
          <Button size="sm" onClick={() => setReloadKey((k) => k + 1)}>
            Retry
          </Button>
        </div>
      )}

      {rowError && (
        <p role="alert" className="rounded border border-danger/60 bg-surface px-3 py-2 text-sm text-danger">
          {rowError}
        </p>
      )}

      {loading && (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      )}

      {agents && agents.length === 0 && !error && (
        <EmptyState
          title="No agents yet"
          description="Create one, give it a prompt and a voice, and talk to it in your browser within a minute."
          action={
            <Button variant="primary" onClick={() => navigate("/agents/new")}>
              Create agent
            </Button>
          }
        />
      )}

      {agents && agents.length > 0 && (
        <Table>
          <THead>
            <TR>
              <TH>Name</TH>
              <TH>Status</TH>
              <TH>Engine</TH>
              <TH>Number</TH>
              <TH align="right">Calls (7d)</TH>
              <TH align="right">Updated</TH>
              <TH align="right">
                <span className="sr-only">Actions</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {agents.map((agent) => (
              <TR
                key={agent.id}
                interactive
                onClick={() => navigate(`/agents/${agent.id}`)}
              >
                <TD className="max-w-[280px]">
                  <div className="flex flex-col gap-0.5 overflow-hidden">
                    <span className="truncate text-bright">{agent.name}</span>
                    {agent.description && (
                      <span className="truncate text-xs text-muted">{agent.description}</span>
                    )}
                  </div>
                </TD>
                <TD>
                  <StatusDot tone={agentStatusTone(agent.status)} label={agent.status} />
                </TD>
                <TD>
                  <Badge mono>
                    {ENGINE_LABELS_SHORT[agent.voice_engine]}
                  </Badge>
                </TD>
                <TD mono>{agent.phone_e164 ? formatPhone(agent.phone_e164) : "—"}</TD>
                <TD align="right" mono>
                  {agent.calls_7d}
                </TD>
                <TD align="right" mono>
                  {formatRelative(agent.updated_at)}
                </TD>
                <TD align="right" className="w-px">
                  {/* Stop propagation so row actions never trigger row navigation. */}
                  <div
                    className="flex justify-end gap-1"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={duplicatingId === agent.id}
                      disabled={duplicatingId !== null && duplicatingId !== agent.id}
                      onClick={() => void handleDuplicate(agent)}
                      aria-label={`Duplicate ${agent.name}`}
                    >
                      Duplicate
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        setDeleteError(null);
                        setDeleteTarget(agent);
                      }}
                      aria-label={`Delete ${agent.name}`}
                    >
                      Delete
                    </Button>
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      <DeleteAgentModal
        open={deleteTarget !== null}
        agentName={deleteTarget?.name ?? "this agent"}
        deleting={deleting}
        error={deleteError}
        onCancel={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
}
